import { addBreadcrumb } from "@sentry/react";
import { captureSentryLog } from "./sentry-logger.js";

type MutationOperation = "remove" | "removeChild" | "replaceChildren" | "replaceWith" | "replaceChild";

interface IDomNodeDescription {
  nodeName: string;
  id?: string;
  className?: string;
  childCount: number;
  isConnected: boolean;
}

interface ILastMutation {
  operation: MutationOperation;
  timestamp: number;
  target: Node;
  relatedNode?: Node;
}

const installMarker = Symbol.for("holisto.domMutationDiagnostics");
const lastMutationByNode = new WeakMap<Node, ILastMutation>();

const describeNode = (node: Node | null | undefined): IDomNodeDescription | null => {
  if (!node) {
    return null;
  }

  const element = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : undefined;
  const className = element?.getAttribute("class")?.trim();
  const id = element?.id.trim();

  return {
    nodeName: node.nodeName,
    ...(id ? { id } : {}),
    ...(className ? { className } : {}),
    childCount: node.childNodes.length,
    isConnected: node.isConnected,
  };
};

const describeMutation = (mutation: ILastMutation | undefined) => {
  if (!mutation) {
    return null;
  }

  return {
    operation: mutation.operation,
    timestamp: mutation.timestamp,
    target: describeNode(mutation.target),
    relatedNode: describeNode(mutation.relatedNode),
  };
};

const rememberMutation = (operation: MutationOperation, target: Node, relatedNode?: Node): void => {
  const mutation = { operation, target, relatedNode, timestamp: Date.now() };
  lastMutationByNode.set(target, mutation);

  if (relatedNode) {
    lastMutationByNode.set(relatedNode, mutation);
  }
};

const isRemoveChildError = (error: unknown): boolean => {
  if (error instanceof DOMException && error.name === "NotFoundError") {
    return true;
  }

  return error instanceof Error && /removeChild.*not.*child/i.test(error.message);
};

const getMutationContext = (parent: Node, child: Node) => ({
  operation: "removeChild",
  parent: describeNode(parent),
  child: describeNode(child),
  actualChildParent: describeNode(child.parentNode),
  parentContainsChild: parent.contains(child),
  parentLastMutation: describeMutation(lastMutationByNode.get(parent)),
  childLastMutation: describeMutation(lastMutationByNode.get(child)),
  route: window.location.pathname,
  queryKeys: [...new URLSearchParams(window.location.search).keys()],
  documentReadyState: document.readyState,
});

const reportRemoveChildFailure = (parent: Node, child: Node, error: unknown): void => {
  if (!isRemoveChildError(error)) {
    return;
  }

  try {
    const context = getMutationContext(parent, child);
    addBreadcrumb({
      category: "dom.mutation",
      level: "error",
      message: "DOM removeChild failed",
      data: context,
    });
    captureSentryLog("warning", "DOM removeChild failed", context);
  } catch {
    // Diagnostics must never change the original DOM exception behavior.
  }
};

export const installDomMutationDiagnostics = (): void => {
  if (typeof window === "undefined" || Node.prototype[installMarker as never]) {
    return;
  }

  Object.defineProperty(Node.prototype, installMarker, { value: true });

  const removeChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (this: Node, child: Node): Node {
    try {
      const result = removeChild.call(this, child);
      rememberMutation("removeChild", this, child);
      return result;
    } catch (error) {
      reportRemoveChildFailure(this, child, error);
      throw error;
    }
  } as typeof Node.prototype.removeChild;

  const replaceChild = Node.prototype.replaceChild;
  Node.prototype.replaceChild = function (this: Node, newChild: Node, oldChild: Node): Node {
    const result = replaceChild.call(this, newChild, oldChild);
    rememberMutation("replaceChild", this, oldChild);
    rememberMutation("replaceChild", newChild, this);
    return result;
  } as typeof Node.prototype.replaceChild;

  const replaceChildren = Element.prototype.replaceChildren;
  Element.prototype.replaceChildren = function (this: Element, ...nodes: (Node | string)[]): void {
    replaceChildren.call(this, ...nodes);
    rememberMutation(
      "replaceChildren",
      this,
      nodes.find((node): node is Node => node instanceof Node),
    );
  };

  const remove = Element.prototype.remove;
  Element.prototype.remove = function (this: Element): void {
    remove.call(this);
    rememberMutation("remove", this);
  };

  const replaceWith = Element.prototype.replaceWith;
  Element.prototype.replaceWith = function (this: Element, ...nodes: (Node | string)[]): void {
    replaceWith.call(this, ...nodes);
    rememberMutation(
      "replaceWith",
      this,
      nodes.find((node): node is Node => node instanceof Node),
    );
  };
};
